import classNames from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useState } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { forTemplate } from '@trello/legacy-i18n';
import { currentLocale } from '@trello/locale';
import { Button } from '@trello/nachos/button';
import { Select } from '@trello/nachos/select';
import { Spinner } from '@trello/nachos/spinner';
import { navigate } from '@trello/router/navigate';
import { RouterLink } from '@trello/router/router-link';

import { Alerts } from 'app/scripts/views/lib/Alerts';
import { LoadingSpinner } from 'app/src/components/LoadingSpinner';
import { useCreatePluginMutation } from './CreatePluginMutation.generated';
import type { CreatePluginTeamsQuery } from './CreatePluginTeamsQuery.generated';
import { useCreatePluginTeamsQuery } from './CreatePluginTeamsQuery.generated';
import { InputErrors } from './InputErrors';
import {
  isEmailOrUrl,
  isEmptyOr,
  isStringInRange,
  isValidHttpsUrl,
} from './pluginValidators';
import {
  useValidatedState,
  useValidatedSyncedState,
} from './useValidatedState';

import styles from './CreatePowerUpPage.less';

const format = forTemplate('app_management');

interface Option {
  label: string;
  value: string;
}

function getDeveloperAgreement(data: CreatePluginTeamsQuery | undefined) {
  const agreements = data?.member?.agreements;
  if (!agreements) {
    return null;
  }

  const developerTerms = agreements.find(
    (agreement) => agreement.agreementType === 'developer-terms',
  );

  if (!developerTerms) {
    return null;
  }

  return developerTerms.id;
}

export const CreatePowerUpPage: FunctionComponent = () => {
  const { data, loading, error } = useCreatePluginTeamsQuery({
    waitOn: ['MemberHeader', 'MemberBoards'],
  });

  const [name, setName, nameErrors] = useValidatedState(
    '',
    isStringInRange(
      1,
      50,
      format('error-must-be-characters-long', {
        min: '1',
        max: '50',
      }),
    ),
  );
  const [iframeConnectorUrl, setIframeConnectorUrl, iframeConnectorUrlErrors] =
    useValidatedState(
      '',
      isValidHttpsUrl(format('error-url-must-be-served-over-https')),
    );

  const [email, setEmail, emailErrors] = useValidatedSyncedState(
    '',
    '',
    isEmptyOr(isEmailOrUrl(format('error-invalid-email'))),
  );
  const [supportEmail, setSupportEmail, supportEmailErrors] =
    useValidatedSyncedState(
      '',
      '',
      isEmptyOr(isEmailOrUrl(format('error-invalid-email-or-url'))),
    );

  const [author, setAuthor, authorErrors] = useValidatedState(
    '',
    isStringInRange(0, 50, format('error-author-length', { max: '50' })),
  );

  const [team, setTeam] = useState<Option | null>(null);

  const [createPlugin, { loading: creating }] = useCreatePluginMutation();

  const onSubmit = useCallback(async () => {
    if (!team) {
      return;
    }

    const traceId = Analytics.startTask({
      taskName: 'edit-plugin/createPowerUp',
      source: 'powerUpsAdminNewScreen',
    });

    try {
      const { data: response } = await createPlugin({
        variables: {
          agreementId: getDeveloperAgreement(data) || '',
          organizationId: team.value,
          author,
          email,
          supportEmail,
          iframeConnectorUrl,
          listings: [
            {
              name,
              locale: currentLocale,
              overview: '',
              description: '',
            },
          ],
        },
      });
      const plugin = response?.createPlugin;
      if (plugin) {
        Analytics.taskSucceeded({
          taskName: 'edit-plugin/createPowerUp',
          traceId,
          source: 'powerUpsAdminNewScreen',
        });
        Alerts.show('power-up-created', 'info', 'powerupcreated', 3000);
        navigate(
          `/power-ups/${plugin.id}/edit${
            iframeConnectorUrl ? '/' : '/api-key'
          }`,
          { trigger: true },
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-shadow
    } catch (error) {
      Analytics.taskFailed({
        taskName: 'edit-plugin/createPowerUp',
        traceId,
        source: 'powerUpsAdminNewScreen',
        error,
      });
      Alerts.show('something-went-wrong', 'error', 4000);
    }
  }, [
    team,
    createPlugin,
    data,
    author,
    email,
    supportEmail,
    iframeConnectorUrl,
    name,
  ]);

  const selectWorkspaceOnChange = useCallback(
    (option: Option) => setTeam(option),
    [setTeam],
  );

  const member = data?.member;
  const organizations = member?.organizations;

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !member || !organizations) {
    return (
      <div className={styles.content}>{format('something-went-wrong')}</div>
    );
  }

  const availableOrgs = member.organizations.filter((organization) => {
    const membership = organization.memberships?.find(
      (m) => m.idMember === member.id,
    );
    if (!membership) {
      return false;
    }
    const { memberType } = membership;
    return memberType === 'admin' || memberType === 'normal';
  });

  const orgOptions = availableOrgs.map((organization) => ({
    label: organization.displayName,
    value: organization.id,
  }));

  const createButtonIsDisabled =
    creating ||
    !(name.length && team) ||
    !!nameErrors ||
    (!!iframeConnectorUrl.length && !!iframeConnectorUrlErrors) ||
    !email.length ||
    !supportEmail.length ||
    !!emailErrors ||
    !!supportEmailErrors ||
    !author.length;

  return (
    <div className={classNames(styles.content, styles.createPowerUpContainer)}>
      <div className={styles.header}>
        <h1>{format('new-powerup-or-integration')}</h1>
      </div>
      <div>
        <div className={styles.createFormSection}>
          <label>
            {format('new-powerup-or-integration')}
            <input
              type="text"
              className={classNames({
                [styles.pluginInput]: true,
                'input-error': name.length && nameErrors,
              })}
              name="name"
              placeholder={format('powerup-or-integration-name')}
              value={name}
              // @ts-expect-error

              onChange={(e) => setName(e.target.value)}
            />
            {name.length ? <InputErrors errors={nameErrors} /> : null}
          </label>
          <p className="quiet">{format('you-can-change-this-later')}</p>
        </div>

        <div className={styles.createFormSection}>
          <label>{format('team')}</label>
          <Select
            blurInputOnSelect={true}
            className={styles.pluginInput}
            options={orgOptions}
            value={team}
            // @ts-expect-error
            onChange={selectWorkspaceOnChange}
          />
          <p className={classNames('quiet', styles.teamDescription)}>
            {format('team-description')}
          </p>
        </div>

        <div className={styles.createFormSection}>
          <label>
            {format('iframe-connector-url-flagged')}
            <input
              type="text"
              className={classNames({
                [styles.pluginInput]: true,
                'input-error':
                  iframeConnectorUrl.length && iframeConnectorUrlErrors,
              })}
              name="iframe-connector-url"
              placeholder="https://weather-power-up.netlify.com/"
              value={iframeConnectorUrl}
              // @ts-expect-error
              onChange={(e) => setIframeConnectorUrl(e.target.value)}
            />
            {iframeConnectorUrl.length ? (
              <InputErrors errors={iframeConnectorUrlErrors} />
            ) : null}
          </label>
          <p
            className="quiet"
            // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
            dangerouslySetInnerHTML={{
              __html: format('iframe-connector-url-description-flagged'),
            }}
          />
        </div>
        <div className={styles.createFormSection}>
          <label>
            {format('email')}
            <input
              type="email"
              className={classNames({
                [styles.pluginInput]: true,
                'input-error': emailErrors,
              })}
              name="email"
              placeholder="me@company.com"
              value={email}
              // @ts-expect-error
              onChange={(e) => setEmail(e.target.value)}
              disabled={false}
            />
            <InputErrors errors={emailErrors} />
          </label>
          <p className="quiet">{format('email-description')}</p>
        </div>
        <div className={styles.createFormSection}>
          <label>
            {format('support-email')}
            <input
              type="text"
              className={classNames({
                [styles.pluginInput]: true,
                'input-error': supportEmailErrors,
              })}
              name="support-email"
              placeholder="support@company.com"
              value={supportEmail}
              // @ts-expect-error
              onChange={(e) => setSupportEmail(e.target.value)}
              disabled={false}
            />
            <InputErrors errors={supportEmailErrors} />
          </label>
          <div className={styles.inputDescription}>
            {format('support-email-description')}
          </div>
        </div>
        <div className={styles.createFormSection}>
          <label>
            {format('author')}
            <input
              type="text"
              className={classNames({
                [styles.pluginInput]: true,
                'input-error': authorErrors,
              })}
              name="author"
              placeholder="Author Name"
              value={author}
              // @ts-expect-error
              onChange={(e) => setAuthor(e.target.value)}
              disabled={false}
            />
            <InputErrors errors={authorErrors} />
          </label>
          <div className={styles.inputDescription}>
            {format('author-description')}
          </div>
        </div>
      </div>
      <div className={classNames(styles.footer, styles.formFooter)}>
        <RouterLink href="/power-ups/admin">
          <Button isDisabled={creating}>{format('cancel')}</Button>
        </RouterLink>
        <Button
          appearance="primary"
          onClick={onSubmit}
          isDisabled={createButtonIsDisabled}
        >
          {creating ? <Spinner small /> : format('create')}
        </Button>
      </div>
    </div>
  );
};
